/**
 * Sprint 23.6 v2 — AgentProfileHeader (Hero section 2: identity strip).
 *
 * Layout per the visual contract: avatar (52) + info column. The info column
 * holds the name row (name + status pill + reports-to pill), then a plain role
 * line, then the bio paragraph. Bio is the ONLY persona-depth field on the hero
 * (Q3/Q16) — inline-editable + sparkle-regenerable; everything else lives in the
 * Settings tab and the sidebar.
 *
 * Section frame: padding 16px 0 14px + a 0.5px bottom border (the hero sections
 * are separated by hairline rules, not flex gaps).
 */

import { useMemo, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { NodeAvatar, EditableTitle, showToast } from "../primitives";
import { useUpdateNode, useUpdateWorkspaceName } from "../../../lib/hooks/use-kuvio";
import { useWorkforceStatus } from "../../hooks/use-workforce-status";
import { useThemedLabels } from "../../hooks/use-themed-labels";
import type { AgentConfig, LibraryNodeDetail } from "../../../lib/types";
import { getAgentPersona } from "../cockpit/CockpitHeader";
import { InlinePersonaEdit } from "../persona/InlinePersonaEdit";
import { SparkleRegen } from "../persona/SparkleRegen";

export interface AgentProfileHeaderProps {
  node: LibraryNodeDetail;
  /** Owner role check — gates inline edit affordances on others' agents (Q26). */
  canEdit: boolean;
}

export function AgentProfileHeader({ node, canEdit }: AgentProfileHeaderProps) {
  const navigate = useNavigate();
  const updateNode = useUpdateNode();
  const updateWorkspaceName = useUpdateWorkspaceName();
  const labels = useThemedLabels();
  const isRoot = !!node.is_root;
  const config = (node.agent_config ?? null) as AgentConfig | null;
  const persona = getAgentPersona({
    path: node.path,
    agent_config: node.agent_config as Record<string, unknown> | null,
  });

  // Reports-to: nearest agent ancestor in the breadcrumb (skip folders).
  const reportsTo = useMemo(() => {
    const trail = node._breadcrumb ?? [];
    for (let i = trail.length - 1; i >= 0; i--) {
      const seg = trail[i]!;
      if (seg.type === "agent") return seg;
    }
    return null;
  }, [node._breadcrumb]);

  // Status pill (Sprint 23 — workforce-status). Single agent, so a tiny query.
  const status = useWorkforceStatus(isRoot ? [] : [node.id], !isRoot);
  const myStatus = status.data?.[node.id] ?? null;

  async function savePersonaPatch(patch: Partial<AgentConfig>) {
    const next = { ...(node.agent_config ?? {}), ...patch } as AgentConfig;
    try {
      await updateNode.mutateAsync({ id: node.id, agent_config: next });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "save failed";
      showToast(`Couldn't save: ${msg}`, "error", true);
      throw err;
    }
  }

  return (
    <section style={wrap}>
      <div style={row}>
        <NodeAvatar type={node.type} persona={persona} size="xl" name={node.name} />
        <div style={info}>
          <div style={nameRow}>
            <EditableTitle
              value={node.name}
              fontSize={19}
              fontWeight={500}
              onSave={async (newName) => {
                try {
                  if (isRoot) await updateWorkspaceName.mutateAsync(newName);
                  else await updateNode.mutateAsync({ id: node.id, name: newName });
                  showToast(`Renamed to '${newName}'`);
                } catch (err) {
                  const msg = err instanceof Error ? err.message : "rename failed";
                  showToast(`Couldn't rename: ${msg}`, "error", true);
                  throw err;
                }
              }}
            />
            {!isRoot && <StatusPill status={myStatus} labels={labels} />}
            {reportsTo && (
              <button
                type="button"
                onClick={() => navigate(`/agents/${reportsTo.id}`)}
                style={reportsToPill}
                title={`${labels.reportsToLabel} ${reportsTo.name}`}
              >
                <i className="ti ti-arrow-up" style={{ fontSize: 11 }} aria-hidden="true" />
                <span>{reportsTo.name}</span>
              </button>
            )}
          </div>

          {/* Role — the one-line description. Display-only on the hero (Q16). */}
          {!isRoot && node.description?.trim() && (
            <div style={roleLine}>{node.description}</div>
          )}

          {/* Bio — the only persona-depth field on the hero (Q3). Inline-edit +
              sparkle regen; renders as a plain secondary paragraph. */}
          {!isRoot && (
            <div style={bioRow}>
              <SparkleRegen
                agentId={node.id}
                agentName={node.name}
                field="bio"
                style={config?.style ?? null}
              >
                {(openRegen) => (
                  <InlinePersonaEdit
                    label="Bio"
                    hideLabel
                    value={config?.bio ?? null}
                    mode="textarea"
                    placeholder="Add a short bio — what makes this hire tick."
                    readOnly={!canEdit}
                    onRegenerate={canEdit ? openRegen : undefined}
                    renderValue={(v) =>
                      typeof v === "string" && v.trim().length > 0 ? (
                        <span style={bioText}>{v}</span>
                      ) : (
                        <span style={bioPlaceholder}>
                          Add a short bio — what makes this hire tick.
                        </span>
                      )
                    }
                    onSave={(next) =>
                      savePersonaPatch({ bio: typeof next === "string" ? next : null })
                    }
                  />
                )}
              </SparkleRegen>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/** Status pill (Q5 states): dot + themed label + verb/duration. */
function StatusPill({
  status,
  labels,
}: {
  status: { status: string; verb: string | null; duration_seconds: number | null } | null;
  labels: ReturnType<typeof useThemedLabels>;
}) {
  if (!status) return null;
  const dur = formatDuration(status.duration_seconds);
  let dotColor: string;
  let textColor: string;
  let bg: string;
  let text: string;
  if (status.status === "working") {
    dotColor = "var(--color-text-success)";
    textColor = "var(--color-text-success)";
    bg = "#EAF3DE";
    text = `${status.verb || labels.statusWorking}${dur ? ` · ${dur}` : ""}`;
  } else if (status.status === "awaiting") {
    dotColor = "var(--color-text-warning)";
    textColor = "var(--color-text-warning)";
    bg = "#FAEEDA";
    text = `${labels.statusAwaiting}${dur ? ` · ${dur}` : ""}`;
  } else {
    dotColor = "var(--color-text-tertiary)";
    textColor = "var(--color-text-secondary)";
    bg = "var(--color-background-secondary)";
    text = `${labels.statusAvailable}${dur ? ` · last ${dur}` : ""}`;
  }
  const weight = status.status === "available" ? 400 : 500;
  return (
    <span style={{ ...statusPill, background: bg, color: textColor, fontWeight: weight }}>
      <span style={{ ...statusDot, background: dotColor }} />
      {text}
    </span>
  );
}

function formatDuration(seconds: number | null): string | null {
  if (seconds == null || seconds < 0) return null;
  const m = Math.floor(seconds / 60);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

const wrap: CSSProperties = {
  padding: "16px 0 14px",
  borderBottom: "0.5px solid var(--color-border-tertiary)",
};

const row: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: 14,
};

const info: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

const nameRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap",
};

const roleLine: CSSProperties = {
  marginTop: 2,
  fontSize: 13,
  color: "var(--color-text-secondary)",
};

const reportsToPill: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "3px 10px",
  fontSize: 12,
  borderRadius: 999,
  border: "0.5px solid var(--color-border-tertiary)",
  background: "var(--color-background-secondary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const statusPill: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "3px 10px",
  borderRadius: 999,
  fontSize: 11,
};

const statusDot: CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: "50%",
  flexShrink: 0,
};

const bioRow: CSSProperties = {
  marginTop: 6,
};

const bioText: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-secondary)",
  lineHeight: 1.55,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const bioPlaceholder: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-tertiary)",
  fontStyle: "italic",
  lineHeight: 1.55,
};
